import { useState } from 'react';
import { useBowProfiles } from '../hooks';
import type { BowProfile } from '../types';
import { newProfileId } from '../storage';
import { BowProfileForm } from './BowProfileForm';
import { useForgeLibrary } from '../useForgeLibrary';

const EMPTY_BOW = (): BowProfile => ({
  profileId: newProfileId(),
  name: '',
  isPrimary: false,
  bow: {
    brand: '',
    model: '',
    iboFps: 342,
    drawWeightLb: 70,
    drawLengthIn: 29,
  },
  sight: {
    housing: '',
    pinDiameterIn: 0.019,
    pinCount: 1,
  },
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

export function BowsPage() {
  const { profiles, upsert, remove, setPrimary } = useBowProfiles();
  const forge = useForgeLibrary();
  const [editing, setEditing] = useState<BowProfile | null>(null);
  const [creating, setCreating] = useState(false);

  function startCreate() {
    setEditing(EMPTY_BOW());
    setCreating(true);
  }

  function startEdit(p: BowProfile) {
    setEditing({ ...p });
    setCreating(false);
  }

  function cancel() {
    setEditing(null);
    setCreating(false);
  }

  function save(p: BowProfile) {
    /* If this is the first profile, make it primary by default */
    const isFirst = profiles.length === 0;
    upsert({ ...p, isPrimary: p.isPrimary || isFirst, updatedAt: Date.now() });
    setEditing(null);
    setCreating(false);
  }

  /* Copy a read-only imported Forge setup into the editable My Bows store.
   * User-initiated only; assigns a fresh profileId. */
  function saveImported(p: BowProfile) {
    const now = Date.now();
    upsert({
      ...p,
      profileId: newProfileId(),
      isPrimary: profiles.length === 0,
      createdAt: now,
      updatedAt: now,
    });
  }

  const localNames = new Set(profiles.map((p) => p.name.trim().toLowerCase()));
  const importable = forge.bows.filter((b) => !localNames.has(b.name.trim().toLowerCase()));

  return (
    <div className="profiles-page bows-page">
      <nav className="forge-nav">
        <a href="https://sparrowexpeditions.com/pages/the-forge" target="_top">The Forge</a>
        <a href="/wind-drift.html">Wind-Drift</a>
        <a href="/build-review.html">Build Review</a>
        <a href="/tune.html">Tune Wizard</a>
        <a href="/range.html">Range Log</a>
        <a href="/bows.html" className="active">My Bows</a>
        <a href="/arrows.html">My Arrows</a>
        <a href="/pages/methods#ashby" target="_blank" rel="noopener">Methods</a>
        <a href="/blogs/field-notes/500-grain-eland-what-ashby-missed" target="_blank" rel="noopener">Field Notes</a>
      </nav>

      <header className="profiles-header">
        <div>
          <h1>My Bows</h1>
          <p className="lede">
            Save your bow rigs. Each profile bundles bow specs + sight. One bow is your
            <strong> primary</strong> — that's the rig that defaults across the Forge.
          </p>
        </div>
        <button type="button" className="primary-cta" onClick={startCreate}>
          + Add Bow
        </button>
      </header>

      {editing && (
        <div className="profile-form-shell">
          <BowProfileForm
            profile={editing}
            mode={creating ? 'create' : 'edit'}
            onSave={save}
            onCancel={cancel}
          />
        </div>
      )}

      <section className="profile-list">
        {profiles.length === 0 ? (
          <div className="empty-state">
            <p>No bows yet. Add your first rig to get started.</p>
            <button type="button" className="primary-cta" onClick={startCreate}>
              + Add your first bow
            </button>
          </div>
        ) : (
          profiles.map((p) => (
            <article key={p.profileId} className={`profile-card ${p.isPrimary ? 'is-primary' : ''}`}>
              <div className="profile-card-head">
                <div>
                  <h2 className="profile-name">
                    {p.name || <em className="unnamed">Untitled bow</em>}
                    {p.isPrimary && <span className="primary-pill">Primary</span>}
                  </h2>
                  <p className="profile-sub">
                    {p.bow.brand} {p.bow.model} · {p.bow.iboFps} IBO · {p.bow.drawWeightLb}lb @ {p.bow.drawLengthIn}″
                  </p>
                  <p className="profile-sub-faint">
                    Sight: {p.sight.housing || '—'} ·{' '}
                    {p.sight.pinDiameterIn ? `${p.sight.pinDiameterIn.toFixed(3)}″ pin` : 'no pin set'}
                  </p>
                </div>
                <div className="profile-actions">
                  {!p.isPrimary && (
                    <button type="button" onClick={() => setPrimary(p.profileId)} title="Set as primary rig">
                      Set primary
                    </button>
                  )}
                  <button type="button" onClick={() => startEdit(p)}>Edit</button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => {
                      if (confirm(`Delete "${p.name}"?`)) remove(p.profileId);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </article>
          ))
        )}
      </section>

      {forge.status === 'ready' && importable.length > 0 && (
        <section className="profile-list forge-import-list">
          <h2 className="forge-import-heading">From your Forge setups</h2>
          <p className="forge-import-note">
            Bow rigs you built in the Forge. These are read-only here —
            tap <strong>Save to My Bows</strong> to make one editable.
          </p>
          {importable.map((p) => (
            <article key={p.profileId} className="profile-card is-imported">
              <div className="profile-card-head">
                <div>
                  <h2 className="profile-name">
                    {p.name || <em className="unnamed">Untitled bow</em>}
                    <span className="imported-pill">Forge</span>
                  </h2>
                  <p className="profile-sub">
                    {p.bow.brand} {p.bow.model} · {p.bow.iboFps} IBO · {p.bow.drawWeightLb}lb @ {p.bow.drawLengthIn}″
                  </p>
                  <p className="profile-sub-faint">
                    Sight: {p.sight.housing || '—'} ·{' '}
                    {p.sight.pinDiameterIn ? `${p.sight.pinDiameterIn.toFixed(3)}″ pin` : 'no pin set'}
                  </p>
                </div>
                <div className="profile-actions">
                  <button type="button" className="primary-cta" onClick={() => saveImported(p)}>
                    Save to My Bows
                  </button>
                </div>
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
